// Wie ben ik — en, belangrijker, wat betekent het als dat niet te beantwoorden
// is.
//
// `useResource` geeft één foutregel terug, en dat is hier te weinig: de drie
// manieren waarop /api/mij níét antwoordt zijn drie verschillende dingen voor de
// gebruiker, en alle drie horen ze hun eigen reden te tonen (W8-ontwerpregel 1).
//
//  · 401 — er is geen inlog. Vandaag de normale stand: de magic link komt in
//    sessie 4 van W1c. Geen fout, en zeker geen "opnieuw inloggen".
//  · 403 — je bent niet uitgenodigd, of je toegang is ingetrokken. De service
//    stuurt daar een Nederlandse reden bij; die tonen we letterlijk.
//  · iets anders — een echte storing.

import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "./api";
import { opAuthWissel } from "./auth";
import type { Mij } from "../contracts";

export type MijStand =
  | { stand: "laden" }
  | { stand: "ingelogd"; mij: Mij }
  | { stand: "geen_inlog" }
  | { stand: "geen_toegang"; reden: string }
  | { stand: "fout"; reden: string };

export function useMij(): MijStand {
  const [stand, setStand] = useState<MijStand>({ stand: "laden" });
  // Elke ronde krijgt zijn eigen nummer, zodat een antwoord van een oudere
  // ronde een nieuwere niet kan overschrijven (uitloggen terwijl de vorige
  // aanroep nog onderweg is).
  const [ronde, setRonde] = useState(0);

  useEffect(() => {
    let levend = true;
    const zet = (s: MijStand) => levend && setStand(s);
    api
      .mij()
      .then((mij) => zet({ stand: "ingelogd", mij }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          zet({ stand: "geen_inlog" });
        } else if (err instanceof ApiError && err.status === 403) {
          zet({ stand: "geen_toegang", reden: err.message || err.reason });
        } else {
          zet({
            stand: "fout",
            reden: err instanceof ApiError ? err.reason : "Onbekende fout bij het ophalen.",
          });
        }
      });
    return () => {
      levend = false;
    };
  }, [ronde]);

  const opnieuw = useCallback(() => setRonde((n) => n + 1), []);

  // Twee redenen om opnieuw te vragen wie ik ben:
  //  · de sessie wisselt — terugkomen van de magic link zet het token ná de
  //    eerste render; zonder dit blijft het scherm staan op "niet ingelogd"
  //    terwijl je dat net wél bent, en uitloggen zou pas na F5 te zien zijn;
  //  · het beheerscherm wijzigde de eigen rij (rol, propositie, krediet).
  useEffect(() => opAuthWissel(opnieuw), [opnieuw]);
  useEffect(() => abonneer(opnieuw), [opnieuw]);

  return stand;
}

// Eén melding, geen store: wie zijn eigen rij wijzigt roept dit aan, en elk
// scherm dat `useMij` gebruikt haalt hem opnieuw op.
const luisteraars = new Set<() => void>();

function abonneer(bij: () => void): () => void {
  luisteraars.add(bij);
  return () => {
    luisteraars.delete(bij);
  };
}

export function meldMijGewijzigd(): void {
  for (const bij of luisteraars) bij();
}

/**
 * Het Plan-tabblad (marktbewerkingsplan, voorproef 30-09) is alleen voor
 * accountmanagement en voor beheerders. Verbergen is geen beveiliging — de
 * pagina leest alleen wat elke ingelogde gebruiker al mag lezen — maar het
 * plan is werkmateriaal van de AM-rol, en zo zegt het menu dat ook.
 */
export function magPlan(mij: Mij | null): boolean {
  // ADR 2026-10-23-ict-los-platform (C3): het Plan is op ICT gebouwd en wordt
  // voor de pilot niet omgebouwd. De service zegt per gebruiker of het tabblad
  // bij zijn startsector hoort (proposities.yaml plan_sectoren); zolang alleen
  // ICT zichtbaar is, is dat voor iedereen "ja".
  // Een link om mee te lezen (?lees=) opent het Plan ook zonder AM- of BP-rol; de server beslist.
  if (mij && typeof window !== "undefined" && /[?&]lees=/.test(window.location.search)) return true;
  return heeftPlanRol(mij) && mij?.planZichtbaar !== false;
}

/** De rollen die aan het Plan werken: de accountmanager (AM) en, sinds de
 *  pilot (26-10), de opleidingsmanager (BP, rol `llo`). */
export const PLAN_ROLLEN: readonly string[] = ["accountmanagement", "llo"];

/** De rol die het Plan mag zien, los van de propositie (ook de drempel van de
 *  schakelaar "Nieuwe weergave"). Beheer, AM en BP. */
export function heeftPlanRol(mij: Mij | null): boolean {
  return !!mij && (mij.isAdmin || (!!mij.rol && PLAN_ROLLEN.includes(mij.rol)));
}

/** De sector (van de run) waarop elk scherm opent: de pilotpropositie van de
 *  gebruiker als die op het dashboard staat, anders ICT (ADR
 *  2026-10-23-ict-los-platform). Zonder /api/mij (laden, geen inlog): ICT. */
export const STANDAARD_STARTSECTOR = "ict";
export function startSector(mij: Mij | null | undefined): string {
  return mij?.startSector || STANDAARD_STARTSECTOR;
}

/** De propositie die voor deze gebruiker vaststaat: een accountmanager of
 *  business partner (geen beheerder) ziet alles op de eigen propositie, zonder
 *  menu om te wisselen. Beheer en alle andere rollen: null. Dit regelt het
 *  scherm; de lees-API blijft open. */
export function vasteSector(mij: Mij | null | undefined): string | null {
  if (!mij || mij.isAdmin || !heeftPlanRol(mij)) return null;
  return mij.startSector || null;
}

/** De sectoren voor funding en arbeidsmarkt. Die registers kennen de
 *  bedrijfstakken van sectors.yaml (ict, maakindustrie, …), niet de
 *  pilotsectoren. De service rekent ze uit bij de startsector
 *  (`mij.registerSectoren`, pilot 26-10): zo krijgt ook een beheerder zonder
 *  school de bedrijfstakken van de propositie waarop zijn schermen openen.
 *
 *  Geen ICT-terugval meer: een pilotgebruiker zag zo ICT-regelingen. Zonder
 *  serverveld (een oudere dienst) geldt de eigen school; zonder iets een lege
 *  lijst, en dan blijft het scherm op zijn eigen standaard. Startsector ICT is
 *  geen terugval maar ICT zelf: ["ict"]. */
export function registerSectoren(mij: Mij | null | undefined): string[] {
  if (mij?.registerSectoren) return [...mij.registerSectoren];
  if (!mij) return [];
  if (startSector(mij) === STANDAARD_STARTSECTOR) return [STANDAARD_STARTSECTOR];
  return [...(mij.sectoren ?? [])];
}

export { useStartSector } from "./sectorkeuze";
